import { GeneralMuiModal } from '@components/shared/general-mui-modal';
import { theme } from '@config/theme';
import { useModalDimensions } from '@hooks/useModalDimensions';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import {
	AccordionProps,
	accordionSummaryClasses,
	AccordionSummaryProps,
	Box,
	CircularProgress,
	styled,
} from '@mui/material';
import classNames from 'classnames';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGuidelines } from 'services/monitoring/monitoringHooks';
import { MonitoringModalProps } from 'types/monitoring';

import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary from '@mui/material/AccordionSummary';

const Accordion = styled((props: AccordionProps) => (
	<MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
	border: 'none',
	'&::before': {
		display: 'none',
	},
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
	<MuiAccordionSummary
		expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
		{...props}
	/>
))(() => ({
	padding: '10px 20px',
	borderRadius: '8px',
	height: '42px',
	minHeight: '42px',
	border: `1px solid ${theme.palette.custom.offWhite}`,
	backgroundColor: 'transparent',
	flexDirection: 'row',
	justifyContent: 'space-between',
	[`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
		transform: 'rotate(90deg)',
	},
	[`& .${accordionSummaryClasses.expandIconWrapper}`]: {
		margin: 0,
	},
	[`& .${accordionSummaryClasses.content}`]: {
		margin: 0,
	},
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
	marginTop: theme.spacing(),
	border: 'none',
}));

const LoadingIndicator = () => (
	<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
		<CircularProgress />
	</Box>
);

export function MonitoringModal({ open, onClose, description }: MonitoringModalProps) {
	const modalDimensions = useModalDimensions();
	const { t } = useTranslation();
	const { data: guideLines, isLoading } = useGuidelines('monitoring');

	const [expanded, setExpanded] = useState<string | false>('');

	const handleChange = (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
		setExpanded(newExpanded ? panel : false);
	};

	return (
		<GeneralMuiModal
			maxWidth="xl"
			title={t('monitoring')}
			styles={{
				paperStyle: { width: modalDimensions.width, height: '60%', padding: 0 },
				contentStyle: { padding: 0 },
			}}
			open={open}
			onClose={onClose}
			content={
				<div className="flex flex-col gap-4 p-[20px]">
					{isLoading ? (
						<LoadingIndicator />
					) : (
						<>
							<p className="text-black font-roboto text-base font-normal leading-[140%]">
								{description}
							</p>
							{guideLines?.map((guideLine: any, guideLineIndex: number) => (
								<div key={guideLineIndex} className="flex flex-col gap-4">
									<h3 className="text-black font-roboto text-base font-semibold leading-[140%] uppercase">
										{guideLine.title}
									</h3>

									{guideLine.description.sections.map((content: any, contentIndex: number) => (
										<Accordion
											sx={{ backgroundColor: 'soilAnalysis', margin: 0 }}
											key={contentIndex}
											expanded={expanded === content.title}
											onChange={handleChange(content.title)}
										>
											<AccordionSummary>
												<p
													className={classNames(
														'text-dark font-roboto text-base font-bold leading-[140%] mr-[8px]',
														{
															'text-primary': expanded === content.title,
														}
													)}
												>
													{content.title}
												</p>
											</AccordionSummary>
											<AccordionDetails
												sx={{ padding: 0, paddingLeft: '24px', margin: 0, marginTop: '10px' }}
											>
												{content.description ? (
													<p className="text-black font-roboto text-base font-normal leading-[140%]">
														{content.description}
													</p>
												) : null}
												{content.bullets && content.bullets.length > 0 && (
													<ul className="list-disc pl-5 mt-2">
														{content.bullets.map((bullet: string, bulletIndex: number) => (
															<li
																key={bulletIndex}
																className="text-black font-roboto text-base font-normal leading-[140%]"
															>
																{bullet}
															</li>
														))}
													</ul>
												)}
											</AccordionDetails>
										</Accordion>
									))}
								</div>
							))}
						</>
					)}
				</div>
			}
		/>
	);
}
